import React, { useState, useCallback, useEffect, useRef } from 'react';
import { FaBook, FaProjectDiagram, FaSearch } from 'react-icons/fa';
import WikiTemplatesTab from './components/WikiTemplatesTab';
import WikiProjectsTab from './components/WikiProjectsTab';
import { useAdminWebSocket } from '../../hooks/useProjectWebSocket';

const STORAGE_KEY = 'wikiEditorState';

/**
 * How many forwarded messages the queue keeps.
 *
 * The tab consumes on every render, so it can never be more than a tick behind;
 * this is only here so a tab left open for a day cannot grow the array without
 * bound.
 */
const LIVE_QUEUE_MAX = 50;

const WikiEditor = () => {
    // Load initial state from localStorage
    const getInitialState = () => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                // Validate parsed data - only use if valid, otherwise return defaults
                const isValidTab = parsed.activeTab === 'templates' || parsed.activeTab === 'projects';
                const isValidSearch = typeof parsed.searchQuery === 'string';
                if (isValidTab && isValidSearch) {
                    return {
                        activeTab: parsed.activeTab,
                        searchQuery: parsed.searchQuery
                    };
                }
            }
        } catch (err) {
            console.error('Failed to load wiki editor state:', err);
        }
        return { activeTab: 'templates', searchQuery: '' };
    };

    const initialState = getInitialState();
    const [activeTab, setActiveTab] = useState(initialState.activeTab);
    const [searchQuery, setSearchQuery] = useState(initialState.searchQuery);

    // Track unsaved changes from child components
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const unsavedChangesRef = useRef(false);

    // Keep ref in sync with state for beforeunload handler
    useEffect(() => {
        unsavedChangesRef.current = hasUnsavedChanges;
    }, [hasUnsavedChanges]);

    // Persist state to localStorage
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({
                activeTab,
                searchQuery
            }));
        } catch (err) {
            console.error('Failed to save wiki editor state:', err);
        }
    }, [activeTab, searchQuery]);

    // Browser beforeunload warning
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (unsavedChangesRef.current) {
                e.preventDefault();
                e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
                return e.returnValue;
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);

    // -----------------------------------------------------------------------
    // Live updates (LIV-13). THIS COMPONENT ONLY HANDLES AND FORWARDS.
    //
    // The tab owns the wiki list, the page list and the open page, so it is the
    // tab that refetches, compares and mounts the banner - the same
    // parent-forwards-a-prop shape §9.6 uses for the TME Wiki, and the shape
    // `onUnsavedChanges` already establishes here.
    //
    // THREE message types, and two of them are easy to miss: `wiki_updated`
    // covers page and wiki writes, but the wiki-to-template LINK is written on
    // the TEMPLATES table, so those lists change on `template_updated` - and the
    // Projects tab's project PICKER is the leads list, which a page write can
    // never change and a lead WON does, so it rides `leads_updated` (Bill,
    // 2026-09-03, option B of Fable's round 2).
    //
    // ⚠️ This closes F1 on the frontend side. Both client-wiki template tables
    // have streamed since the day they were created and NOTHING has ever
    // consumed their records: they are keyed on wikiId with no projectId, so
    // every record fell out of the stream handler's routing loop.
    // -----------------------------------------------------------------------
    // ⛔ A QUEUE, NOT A SLOT. `setLiveMessage(message)` looks right and is not:
    // React batches state updates, so several messages arriving in one tick
    // collapse to the LAST one and the tab never sees the others. A template
    // editor emits one `template_updated` PER ROW, so that burst would swallow
    // a `wiki_updated` sitting in front of it - and on the Projects tab that
    // loses a page save outright. Found by Fable's build review, 2026-09-03.
    //
    // Each entry carries a sequence number and the tab consumes everything
    // above the last one it saw, so the queue can be trimmed without the tab
    // losing its place.
    const [liveMessages, setLiveMessages] = useState([]);
    const liveSeqRef = useRef(0);

    useAdminWebSocket(useCallback((message) => {
        // GATE 0 belongs to the TAB too, inside its runner at the point of
        // refetch - this component issues no request of its own (Fable, round
        // 2: per message here it renewed for messages the tab ignores and
        // missed the tab's deferred flush entirely).
        // GATE 1 - type. GATE 2 and GATE 3 belong to the tab, which knows what
        // it has open and whether it is dirty.
        if (message.type !== 'wiki_updated' && message.type !== 'template_updated'
            && message.type !== 'leads_updated') return;
        liveSeqRef.current += 1;
        const entry = { seq: liveSeqRef.current, message };
        setLiveMessages(queue => [...queue, entry].slice(-LIVE_QUEUE_MAX));
    }, []));

    // Handle tab switch with unsaved changes warning
    const handleTabSwitch = useCallback((newTab) => {
        if (hasUnsavedChanges) {
            if (!window.confirm('You have unsaved changes. Discard them and switch tabs?')) {
                return;
            }
        }
        setActiveTab(newTab);
        setHasUnsavedChanges(false);
    }, [hasUnsavedChanges]);

    return (
        <div className="h-full flex flex-col overflow-hidden">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-6">
                    {/* Tab Buttons */}
                    <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                        <button
                            onClick={() => handleTabSwitch('templates')}
                            className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colours ${
                                activeTab === 'templates'
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            <FaBook className="w-4 h-4" />
                            Templates
                        </button>
                        <button
                            onClick={() => handleTabSwitch('projects')}
                            className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colours border-l border-gray-300 ${
                                activeTab === 'projects'
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            <FaProjectDiagram className="w-4 h-4" />
                            Projects
                        </button>
                    </div>
                </div>

                {/* Search */}
                <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search pages..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-64"
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            &times;
                        </button>
                    )}
                </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-hidden">
                {activeTab === 'templates' && (
                    <WikiTemplatesTab
                        searchQuery={searchQuery}
                        onUnsavedChanges={setHasUnsavedChanges}
                        liveMessages={liveMessages}
                    />
                )}
                {activeTab === 'projects' && (
                    <WikiProjectsTab
                        searchQuery={searchQuery}
                        onUnsavedChanges={setHasUnsavedChanges}
                        liveMessages={liveMessages}
                    />
                )}
            </div>
        </div>
    );
};

export default WikiEditor;
